/* eslint-disable max-lines -- Pre-existing violation */
import {Grid, Show, Spinner, Text, VStack} from 'platform/foundation';
import {AlignStyle} from 'quill/formats/align';
import ImageBlot from 'quill/formats/image';
import type Toolbar from 'quill/modules/toolbar';

import {
  ComponentProps,
  ForwardedRef,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, {Quill} from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {identity, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, useLatest} from 'shared/web';

import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {TextEditorFrame} from '../TextEditorFrame/TextEditorFrame';
import {TextEditorOverlays} from '../TextEditorFrame/TextEditorOverlays';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {ACCEPTED_IMAGE_MIME_TYPES} from './ACCEPTED_IMAGE_MIME_TYPES';
import {
  EmojiButton,
  EmojiCategoryButton,
  EmojiCategoryTabs,
  EmojiPanel,
  EmojiPickerWrapper,
} from './components/EmojiPickerWrapper';
import {FooterBar, FooterContent, FooterCounter} from './components/FooterBar';
import {
  ImageMoveIndicator,
  ImageResizeCorner,
  ImageResizeHandle,
  ImageResizeOverlay,
} from './components/ImageResizeOverlay';
import {StyledQuillWrapper} from './components/StyledQuillWrapper';
import {ToolbarTooltip} from './components/ToolbarTooltip';
import {ToolbarUploadingSpinner} from './components/ToolbarUploadingSpinner';
import {useRichTextEditor} from './hooks/useRichTextEditor';
import {useRichTextImageInteractions} from './hooks/useRichTextImageInteractions';
import {useTemplateTokens} from './hooks/useTemplateTokens';
import {useTemplateTokenSuggestions} from './hooks/useTemplateTokenSuggestions';
import {TemplateTokenBlot} from './templateTokens/TemplateTokenBlot';
import {TemplateTokenSuggestionMenu} from './templateTokens/TemplateTokenSuggestionMenu';
import {RichTextProps, RichTextToolbarOptionWithoutImage, ToolbarOption} from './types';
import {buildCounterLabel} from './utils/buildCounterLabel';
import {createRichTextEditorApi} from './utils/createRichTextEditorApi';
import {findEditorImageBySrc} from './utils/findEditorImageBySrc';

class ImageWithLocalPreview extends ImageBlot {
  static sanitize(url: string) {
    return url.startsWith('blob:') ? url : super.sanitize(url);
  }
}

if (isNotNil(Quill)) {
  Quill.register(AlignStyle, true);
  Quill.register(ImageWithLocalPreview, true);
  // Reachable only in an editor whose `formats` names it: Quill builds a registry per editor.
  Quill.register(TemplateTokenBlot, true);
}

const EMOJI_DATA = [
  {key: 'smileys', emojis: ['😀', '😄', '😉', '😊', '😍', '🤝']},
  {key: 'nature', emojis: ['🌞', '🌿', '🌲', '🌷', '🍀', '🔥']},
  {key: 'transport', emojis: ['🚗', '🚙', '🛻', '🏎️', '🛞', '⛽']},
  {key: 'symbols', emojis: ['⭐', '✅', '⚠️', '💡', '📌', '🛠️']},
] as const;

const ALL_TOOLBAR_OPTIONS: ToolbarOption[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'blockquote',
  'listOrdered',
  'listBullet',
  'align',
  'clean',
  'emoji',
  'link',
  'image',
];

const DEFAULT_TOOLBAR_OPTIONS_WITHOUT_IMAGE: RichTextToolbarOptionWithoutImage[] =
  ALL_TOOLBAR_OPTIONS.filter(
    (option): option is RichTextToolbarOptionWithoutImage => option !== 'image'
  );

const TOOLBAR_OPTION_TO_FORMAT: Partial<Record<ToolbarOption, string>> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strike: 'strike',
  header: 'header',
  blockquote: 'blockquote',
  listOrdered: 'list',
  listBullet: 'list',
  align: 'align',
  link: 'link',
  image: 'image',
};

const IMAGE_RESIZE_CORNERS: ImageResizeCorner[] = [
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
];

const DEFAULT_IMAGE_WIDTH = 200;

type QuillEditor = ReturnType<ReactQuill['getEditor']>;
type ToolbarModule = Toolbar & {
  __richTextSkipsNullRange?: boolean;
};

function patchToolbarNullRangeUpdate(editor: QuillEditor) {
  const toolbar = editor.getModule('toolbar') as ToolbarModule | null;

  if (!toolbar || toolbar.__richTextSkipsNullRange) {
    return;
  }

  const updateToolbar = toolbar.update.bind(toolbar);

  toolbar.update = (range) => {
    if (!range) {
      return;
    }

    updateToolbar(range);
  };
  toolbar.__richTextSkipsNullRange = true;
}

function handleToolbarMouseDown(event: MouseEvent<HTMLDivElement>) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const button = target.closest('button');
  if (button && event.currentTarget.contains(button)) {
    event.preventDefault();
  }
}

export const RichText = forwardRef(
  // eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
  (props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) => {
    const t = useTranslationContext();
    const quillRef = useRef<ReactQuill>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const imageInteractionsCleanupRef = useRef<(() => void) | null>(null);
    const editorApiCleanupRef = useRef<(() => void) | null>(null);

    const handleUploadImageRef = useLatest(props.handleUploadImage);
    const onImageUploadPendingChangeRef = useLatest(props.onImageUploadPendingChange);
    const onEditorReadyRef = useLatest(props.onEditorReady);

    const {
      selectedImage,
      isMovingImage,
      handleOverlayRef,
      handleMoveIndicatorRef,
      handleImageResizeDragStart,
      attachImageInteractions,
      clearImageSelectionUnlessResizing,
    } = useRichTextImageInteractions(quillRef, wrapperRef);

    const {
      id,
      helperId,
      toolbarId,
      isFocused,
      currentLength,
      isOverLimit,
      isNearLimit,
      handleFocus,
      handleBlur,
      onChange,
    } = useRichTextEditor(props, userRef);

    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [activeEmojiCategory, setActiveEmojiCategory] = useState(0);
    const [selectionPathKey, setSelectionPathKey] = useState('richText.formats.paragraph');
    const [pendingUploadCount, setPendingUploadCount] = useState(0);

    const isUploadingImage = pendingUploadCount > 0;

    const pendingUploadCountRef = useRef(0);
    const changePendingUploadCount = useCallback(
      (delta: number) => {
        const next = pendingUploadCountRef.current + delta;
        pendingUploadCountRef.current = next;
        setPendingUploadCount(next);
        if (delta > 0 && next === 1) {
          onImageUploadPendingChangeRef.current?.(true);
        }
        if (delta < 0 && next === 0) {
          onImageUploadPendingChangeRef.current?.(false);
        }
      },
      [onImageUploadPendingChangeRef]
    );

    const enabledOptions =
      props.toolbarOptions ??
      (isNotNil(props.handleUploadImage)
        ? ALL_TOOLBAR_OPTIONS
        : DEFAULT_TOOLBAR_OPTIONS_WITHOUT_IMAGE);
    const enabledOptionSet = new Set<ToolbarOption>(enabledOptions);
    const isImageFeatureEnabled =
      enabledOptionSet.has('image') && isNotNil(props.handleUploadImage);
    const isImageFeatureEnabledRef = useLatest(isImageFeatureEnabled);

    const editorBoundsId = `${toolbarId}-editor`;

    const externalValue = props.value ?? '';
    const [lastExternalValue, setLastExternalValue] = useState(externalValue);
    const [quillValue, setQuillValue] = useState(externalValue);
    const templateTokens = useTemplateTokens(
      props.templateTokens,
      lastExternalValue,
      setQuillValue
    );
    if (lastExternalValue !== externalValue) {
      setLastExternalValue(externalValue);
      if (templateTokens.shouldLoadExternalValue(externalValue)) {
        setQuillValue(externalValue);
      }
    }
    const templateTokenSession = templateTokens.session;
    const {menu: templateTokenMenu, menuState: templateTokenMenuState} =
      useTemplateTokenSuggestions(props.templateTokens?.suggestions, wrapperRef);

    // ReactQuill rebuilds Quill when `formats` change without calling its ref again. As the key,
    // the formats remount it, so the ref binds the editor API and token session to the new editor.
    const quillFormatsKey = [
      ...enabledOptions.map((option) => TOOLBAR_OPTION_TO_FORMAT[option]),
      templateTokens.formatName,
    ]
      .filter((format): format is string => isNotNil(format))
      .join('|');

    const quillFormats = useMemo(
      () => (quillFormatsKey ? Array.from(new Set(quillFormatsKey.split('|'))) : []),
      [quillFormatsKey]
    );

    const emojiCategories = useMemo(
      () =>
        EMOJI_DATA.map((category) => ({
          label: t(`richText.emojiCategories.${category.key}`),
          emojis: category.emojis,
        })),
      [t]
    );

    const handleChange: ComponentProps<typeof ReactQuill>['onChange'] = (value, _delta, source) => {
      setQuillValue(value);
      if (source !== 'user') {
        return;
      }
      if (isNil(templateTokenSession)) {
        onChange?.(value);
        return;
      }
      const canonicalValue = templateTokenSession.readUserChange(value);
      if (isNotNil(canonicalValue)) {
        templateTokens.acceptUserValue(canonicalValue);
        onChange?.(canonicalValue);
      }
    };

    const handleQuillRef = useCallback(
      (instance: ReactQuill | null) => {
        quillRef.current = instance;

        if (isNil(instance)) {
          imageInteractionsCleanupRef.current?.();
          imageInteractionsCleanupRef.current = null;
          editorApiCleanupRef.current?.();
          editorApiCleanupRef.current = null;
          onEditorReadyRef.current?.(null);
          return;
        }

        const editor = instance.getEditor();
        patchToolbarNullRangeUpdate(editor);
        imageInteractionsCleanupRef.current = attachImageInteractions(editor);

        const editorApi = createRichTextEditorApi(
          editor,
          templateTokenSession?.toCanonical ?? identity
        );
        const detachTemplateTokens = templateTokenSession?.attach(editor);
        const detachTemplateTokenMenu = templateTokenMenu?.attach(editor);
        editorApiCleanupRef.current = () => {
          editorApi.dispose();
          detachTemplateTokens?.();
          detachTemplateTokenMenu?.();
        };
        onEditorReadyRef.current?.(editorApi.api);
      },
      [attachImageInteractions, onEditorReadyRef, templateTokenSession, templateTokenMenu]
    );

    const handleSelectionChange = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) {
        return;
      }
      const range = editor.getSelection();
      if (!range) {
        return;
      }
      const formats = editor.getFormat(range);
      if (formats.header === 1) {
        setSelectionPathKey('richText.formats.heading1');
      } else if (formats.header === 2) {
        setSelectionPathKey('richText.formats.heading2');
      } else if (formats.header === 3) {
        setSelectionPathKey('richText.formats.heading3');
      } else if (formats.blockquote) {
        setSelectionPathKey('richText.formats.quote');
      } else if (formats.list === 'ordered') {
        setSelectionPathKey('richText.toolbar.numberedList');
      } else if (formats.list === 'bullet') {
        setSelectionPathKey('richText.toolbar.bulletedList');
      } else {
        setSelectionPathKey('richText.formats.paragraph');
      }
    }, []);

    const handleEmojiInsert = useCallback((emoji: string) => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const selection = editor.getSelection();
        const index = selection ? selection.index : editor.getLength();
        editor.insertText(index, emoji, 'user');
        editor.setSelection(index + emoji.length);
      }
      setIsEmojiPickerOpen(false);
    }, []);

    const settleUploadedImage = useCallback(
      (previewUrl: string, hostedUrl: string | null) => {
        URL.revokeObjectURL(previewUrl);
        changePendingUploadCount(-1);
        const editor = quillRef.current?.getEditor();
        if (isNil(editor)) {
          return;
        }
        const image = findEditorImageBySrc(editor, previewUrl);
        if (isNil(image)) {
          return;
        }
        const blot = Quill.find(image);
        if (isNil(blot) || blot instanceof Quill) {
          return;
        }
        const index = editor.getIndex(blot);
        const widthAttribute = image.getAttribute('width');
        editor.deleteText(index, 1, 'user');
        if (isNil(hostedUrl)) {
          return;
        }
        editor.insertEmbed(index, 'image', hostedUrl, 'user');
        if (isNotNil(widthAttribute)) {
          editor.formatText(index, 1, {width: widthAttribute, height: false}, 'user');
        }
      },
      [changePendingUploadCount]
    );

    const insertImageFile = useCallback(
      (file: File, requestedIndex: number | null) => {
        const editor = quillRef.current?.getEditor();
        const uploadHandler = handleUploadImageRef.current;
        if (isNil(editor) || isNil(uploadHandler)) {
          return;
        }

        const selection = editor.getSelection();
        const insertIndex = requestedIndex ?? (selection ? selection.index : editor.getLength());
        const previewUrl = URL.createObjectURL(file);

        const insertPreviewAtIndex = (width: number | null) => {
          editor.insertEmbed(insertIndex, 'image', previewUrl, 'user');
          if (isNotNil(width)) {
            editor.formatText(insertIndex, 1, {width: String(width), height: false}, 'user');
          }
          editor.setSelection(insertIndex + 1);
          changePendingUploadCount(1);
          Promise.resolve()
            .then(() => uploadHandler(file))
            .then(
              (hostedUrl) => {
                const probe = new Image();
                probe.addEventListener('load', () => settleUploadedImage(previewUrl, hostedUrl));
                probe.addEventListener('error', () => settleUploadedImage(previewUrl, hostedUrl));
                probe.src = hostedUrl;
              },
              () => settleUploadedImage(previewUrl, null)
            );
        };

        const probe = new Image();
        probe.addEventListener('load', () =>
          insertPreviewAtIndex(
            probe.naturalWidth > DEFAULT_IMAGE_WIDTH ? DEFAULT_IMAGE_WIDTH : null
          )
        );
        probe.addEventListener('error', () => insertPreviewAtIndex(null));
        probe.src = previewUrl;
      },
      [settleUploadedImage, changePendingUploadCount, handleUploadImageRef]
    );

    const insertImageFileRef = useLatest(insertImageFile);

    const handleImageInsert = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (isNil(editor)) {
        return;
      }

      const selection = editor.getSelection();
      const insertIndex = selection ? selection.index : editor.getLength();

      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = ACCEPTED_IMAGE_MIME_TYPES.join(',');
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (isNil(file)) {
          return;
        }
        insertImageFileRef.current(file, insertIndex);
      });
      fileInput.click();
    }, [insertImageFileRef]);

    const modules = useMemo(
      () => ({
        toolbar: {
          container: `#${toolbarId}`,
          handlers: {
            // The Quill list handler reads the selection without focus, so it fails when the editor is not focused.
            list(this: {quill: QuillEditor}, value: string | false) {
              this.quill.getSelection(true);
              this.quill.format('list', value, 'user');
            },
          },
        },
        uploader: {
          handler: (range: {index: number} | null, files: File[]) => {
            if (!isImageFeatureEnabledRef.current) {
              return;
            }
            files
              .filter((file) => ACCEPTED_IMAGE_MIME_TYPES.includes(file.type))
              .forEach((file) => insertImageFileRef.current(file, range?.index ?? null));
          },
        },
        ...(isNil(templateTokenSession)
          ? {}
          : {clipboard: {matchers: templateTokenSession.clipboardMatchers}}),
      }),
      [toolbarId, insertImageFileRef, isImageFeatureEnabledRef, templateTokenSession]
    );

    const handleEditorBlur = useCallback(() => {
      handleBlur();
      clearImageSelectionUnlessResizing();
    }, [handleBlur, clearImageSelectionUnlessResizing]);

    const isImageSelected = isImageFeatureEnabled && isNotNil(selectedImage);

    // The open "/" menu takes its keys before the consumer capture handler and Quill see them.
    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
      if (templateTokenMenu?.handleKeyDown(event)) {
        return;
      }
      props.onKeyDownCapture?.(event);
    };

    return (
      <div onFocus={props.onFocus} onBlur={props.onBlur} onKeyDown={props.onKeyDown}>
        <VStack>
          <Label
            id={id}
            isRequired={props.isRequired}
            tooltip={props.tooltip}
            isRecommended={props.isRecommended}
            data-testid={suffixTestId('label', props)}
            actions={props.labelActions}
            maxLength={props.maxLength}
            isCounterVisible={props.isCounterVisible}
            currentLength={currentLength}
          >
            {props.label}
          </Label>
          <TextEditorFrame
            onKeyDownCapture={handleKeyDownCapture}
            $isDisabled={props.isDisabled}
            $isInvalid={props.isInvalid || !!props.errorMessage}
            $isClipped
          >
            <StyledQuillWrapper
              ref={wrapperRef}
              id={editorBoundsId}
              $linkLabels={{
                enterLink: t('richText.link.enterLink'),
                visitUrl: t('richText.link.visitUrl'),
                edit: t('general.actions.edit'),
                save: t('general.actions.save'),
                remove: t('general.actions.remove'),
              }}
              $isFocused={isFocused}
              $isDisabled={props.isDisabled}
              $isInvalid={props.isInvalid || !!props.errorMessage}
              $isScrollable={props.isScrollable}
              $rows={props.rows}
              $minRows={props.minRows}
              $maxRows={props.maxRows}
            >
              <div
                id={toolbarId}
                role="toolbar"
                aria-label={t('richText.toolbar.formattingToolbar')}
                onMouseDown={handleToolbarMouseDown}
              >
                <Show
                  when={
                    enabledOptionSet.has('bold') ||
                    enabledOptionSet.has('italic') ||
                    enabledOptionSet.has('underline') ||
                    enabledOptionSet.has('strike')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('bold')}>
                      <ToolbarTooltip label={t('richText.toolbar.bold')}>
                        <button
                          className="ql-bold"
                          type="button"
                          aria-label={t('richText.toolbar.bold')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('italic')}>
                      <ToolbarTooltip label={t('richText.toolbar.italic')}>
                        <button
                          className="ql-italic"
                          type="button"
                          aria-label={t('richText.toolbar.italic')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('underline')}>
                      <ToolbarTooltip label={t('richText.toolbar.underline')}>
                        <button
                          className="ql-underline"
                          type="button"
                          aria-label={t('richText.toolbar.underline')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('strike')}>
                      <ToolbarTooltip label={t('richText.toolbar.strikethrough')}>
                        <button
                          className="ql-strike"
                          type="button"
                          aria-label={t('richText.toolbar.strikethrough')}
                        />
                      </ToolbarTooltip>
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('header') || enabledOptionSet.has('blockquote')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('header')}>
                      {/* Quill's snow theme inserts the generated `.ql-picker` next to the
                          hidden select, i.e. inside this tooltip wrapper, so hover works. */}
                      <ToolbarTooltip label={t('richText.toolbar.heading')}>
                        <select
                          className="ql-header"
                          defaultValue=""
                          aria-label={t('richText.toolbar.heading')}
                        >
                          <option value="1">{t('richText.formats.heading1')}</option>
                          <option value="2">{t('richText.formats.heading2')}</option>
                          <option value="3">{t('richText.formats.heading3')}</option>
                          <option value="">{t('richText.formats.paragraph')}</option>
                        </select>
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('blockquote')}>
                      <ToolbarTooltip label={t('richText.toolbar.blockquote')}>
                        <button
                          className="ql-blockquote"
                          type="button"
                          aria-label={t('richText.toolbar.blockquote')}
                        />
                      </ToolbarTooltip>
                    </Show>
                  </span>
                </Show>
                <Show
                  when={
                    enabledOptionSet.has('listOrdered') ||
                    enabledOptionSet.has('listBullet') ||
                    enabledOptionSet.has('align')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('listOrdered')}>
                      <ToolbarTooltip label={t('richText.toolbar.numberedList')}>
                        <button
                          className="ql-list"
                          value="ordered"
                          type="button"
                          aria-label={t('richText.toolbar.numberedList')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('listBullet')}>
                      <ToolbarTooltip label={t('richText.toolbar.bulletedList')}>
                        <button
                          className="ql-list"
                          value="bullet"
                          type="button"
                          aria-label={t('richText.toolbar.bulletedList')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('align')}>
                      <ToolbarTooltip label={t('richText.toolbar.alignLeft')}>
                        <button
                          className="ql-align"
                          value=""
                          type="button"
                          aria-label={t('richText.toolbar.alignLeft')}
                        />
                      </ToolbarTooltip>
                      <ToolbarTooltip label={t('richText.toolbar.alignCenter')}>
                        <button
                          className="ql-align"
                          value="center"
                          type="button"
                          aria-label={t('richText.toolbar.alignCenter')}
                        />
                      </ToolbarTooltip>
                      <ToolbarTooltip label={t('richText.toolbar.alignRight')}>
                        <button
                          className="ql-align"
                          value="right"
                          type="button"
                          aria-label={t('richText.toolbar.alignRight')}
                        />
                      </ToolbarTooltip>
                      <ToolbarTooltip label={t('richText.toolbar.justify')}>
                        <button
                          className="ql-align"
                          value="justify"
                          type="button"
                          aria-label={t('richText.toolbar.justify')}
                        />
                      </ToolbarTooltip>
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('link') || enabledOptionSet.has('image')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('link')}>
                      <ToolbarTooltip label={t('richText.toolbar.insertLink')}>
                        <button
                          className="ql-link"
                          type="button"
                          aria-label={t('richText.toolbar.insertLink')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={isImageFeatureEnabled}>
                      <ToolbarTooltip label={t('richText.toolbar.insertImage')}>
                        <button
                          type="button"
                          aria-label={t('richText.toolbar.insertImage')}
                          data-testid={suffixTestId('insertImageButton', props)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleImageInsert}
                        >
                          <svg viewBox="0 0 18 18">
                            <rect className="ql-stroke" height="10" width="12" x="3" y="4" />
                            <circle className="ql-fill" cx="6" cy="7" r="1" />
                            <polyline
                              className="ql-even ql-fill"
                              points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"
                            />
                          </svg>
                        </button>
                      </ToolbarTooltip>
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('clean') || enabledOptionSet.has('emoji')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('clean')}>
                      <ToolbarTooltip label={t('richText.toolbar.clearFormatting')}>
                        <button
                          className="ql-clean"
                          type="button"
                          aria-label={t('richText.toolbar.clearFormatting')}
                        />
                      </ToolbarTooltip>
                    </Show>
                    <Show when={enabledOptionSet.has('emoji')}>
                      <EmojiPickerWrapper>
                        <ToolbarTooltip
                          label={t('richText.toolbar.insertEmoji')}
                          isDisabled={isEmojiPickerOpen}
                        >
                          <button
                            className="ql-emoji"
                            type="button"
                            aria-label={t('richText.toolbar.insertEmoji')}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setIsEmojiPickerOpen((v) => !v)}
                          >
                            😀
                          </button>
                        </ToolbarTooltip>
                        <Show when={isEmojiPickerOpen && !props.isDisabled}>
                          <EmojiPanel data-testid={suffixTestId('emojiPicker', props)}>
                            <EmojiCategoryTabs>
                              {emojiCategories.map((category, categoryIndex) => (
                                <EmojiCategoryButton
                                  key={category.label}
                                  role="button"
                                  tabIndex={0}
                                  $isActive={categoryIndex === activeEmojiCategory}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setActiveEmojiCategory(categoryIndex)}
                                >
                                  {category.label}
                                </EmojiCategoryButton>
                              ))}
                            </EmojiCategoryTabs>
                            <Grid columns={6} spacing={1}>
                              {emojiCategories[activeEmojiCategory].emojis.map((emoji) => (
                                <EmojiButton
                                  key={emoji}
                                  role="button"
                                  tabIndex={0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleEmojiInsert(emoji)}
                                >
                                  {emoji}
                                </EmojiButton>
                              ))}
                            </Grid>
                          </EmojiPanel>
                        </Show>
                      </EmojiPickerWrapper>
                    </Show>
                  </span>
                </Show>
                <Show when={isUploadingImage}>
                  <ToolbarUploadingSpinner
                    data-testid={suffixTestId('uploadingImagesSpinner', props)}
                  >
                    <ToolbarTooltip label={t('richText.toolbar.uploadingImages')}>
                      <Spinner size="xSmall" />
                    </ToolbarTooltip>
                  </ToolbarUploadingSpinner>
                </Show>
              </div>
              <ReactQuill
                key={quillFormatsKey}
                ref={handleQuillRef}
                theme="snow"
                value={quillValue}
                onChange={handleChange}
                readOnly={props.isDisabled}
                placeholder={props.placeholder ?? ''}
                modules={modules}
                formats={quillFormats}
                bounds={`#${editorBoundsId}`}
                useSemanticHTML={false}
                onFocus={handleFocus}
                onBlur={handleEditorBlur}
                onChangeSelection={handleSelectionChange}
                aria-labelledby={id}
                aria-describedby={props.errorMessage || props.helperText ? helperId : undefined}
                aria-invalid={props.isInvalid || !!props.errorMessage || undefined}
              />
              <Show
                when={
                  isNotNil(templateTokenMenu) &&
                  isNotNil(templateTokenMenuState) &&
                  !props.isDisabled
                    ? {menu: templateTokenMenu, state: templateTokenMenuState}
                    : null
                }
              >
                {(open) => (
                  <TemplateTokenSuggestionMenu
                    state={open.state}
                    onSelect={open.menu.select}
                    onHighlight={open.menu.highlight}
                    onDismiss={open.menu.dismiss}
                    data-testid={props['data-testid']}
                  />
                )}
              </Show>
              <Show when={isImageSelected && !props.isDisabled}>
                <ImageResizeOverlay
                  ref={handleOverlayRef}
                  data-testid={suffixTestId('imageResizeOverlay', props)}
                >
                  {IMAGE_RESIZE_CORNERS.map((corner) => (
                    <ImageResizeHandle
                      key={corner}
                      $corner={corner}
                      data-testid={suffixTestId(`imageResizeHandle-${corner}`, props)}
                      onPointerDown={(event) => handleImageResizeDragStart(event, corner)}
                    />
                  ))}
                </ImageResizeOverlay>
              </Show>
              <Show when={isMovingImage}>
                <ImageMoveIndicator
                  ref={handleMoveIndicatorRef}
                  data-testid={suffixTestId('imageMoveIndicator', props)}
                />
              </Show>
            </StyledQuillWrapper>
            <FooterBar>
              <Text size="xSmall" color="secondary">
                {t(selectionPathKey)}
              </Text>
              <Show when={isNotNil(props.footerContent)}>
                <FooterContent>{props.footerContent}</FooterContent>
              </Show>
              <Show when={props.isCounterVisible !== false}>
                <FooterCounter $isOverLimit={isOverLimit} $isNearLimit={isNearLimit}>
                  {buildCounterLabel(currentLength, props.maxLength)}
                </FooterCounter>
              </Show>
            </FooterBar>
            <Show
              when={
                !props.isDisabled &&
                (isNotNil(props.selectionContent) || isNotNil(props.editorHighlights))
              }
            >
              <TextEditorOverlays
                getEditor={() => quillRef.current?.getEditor().root ?? null}
                label={props.label}
                selectionContent={props.selectionContent}
                editorHighlights={props.editorHighlights}
              />
            </Show>
            {props.editorContent}
          </TextEditorFrame>
          <Show when={props.errorMessage || props.helperText}>
            <div id={helperId}>
              <HelperText
                errorMessage={props.errorMessage}
                helperText={props.helperText}
                data-testid={suffixTestId('helper', props)}
              />
            </div>
          </Show>
        </VStack>
      </div>
    );
  }
);
